/* eslint-disable */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import notification from '../components/notification';

export const API = createApi({
  reducerPath: 'API',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_URL,
    prepareHeaders: (headers) => {
      if (localStorage.getItem('token')) headers.set('Authorization', localStorage.getItem('token'));
      return headers;
    },
  }),
  tagTypes: ['test', 'panel', 'lab', "labUser"],
  endpoints: (builder) => ({
    login: builder.mutation({
      query: ({ email, password, role }) => ({
        url: '/users/login',
        method: 'POST',
        body: {
          email,
          password,
          deviceType: 0,
          uuid: localStorage.getItem('uuid'),
          role,
        },
      }),
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled.catch(() => notification({
          title: 'Invalid credentials!',
          description: 'Either your email or password is invalid',
          type: 'error',
        }));
      },
    }),
    verifyTwoFactorAuth: builder.mutation({
      query: ({ authCode, token }) => ({
        url: '/users/verifyTwoFactorAuth',
        method: 'PUT',
        headers: { Authorization: token },
        body: { authCode, uuid: localStorage.getItem('uuid') },
      }),
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled.catch((error) => notification({
          title: 'Invalid OTP!',
          description: 'Please enter correct otp',
        }));
      },
    }),
    forgotPassword: builder.mutation({
      query: ({ email, role }) => ({
        url: '/users/forgotPassword',
        method: 'PUT',
        body: { email, role },
      }),
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled
          .then(() => notification({
            title: 'Email sent successfully!',
            description:
              'Check your email for directions to reset your password',
            type: 'success',
          }))
          .catch(() => notification({
            title: 'Invalid Email!',
            description: 'No user found with that email',
            type: 'error',
          }));
      },
    }),
    resetPassword: builder.mutation({
      query: ({ forgotPasswordToken, password, confirmPassword }) => ({
        url: '/users/resetPassword',
        method: 'PUT',
        body: { forgotPasswordToken, password, confirmPassword },
      }),
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled
          .then(() => notification({
            title: 'Password Updated!',
            description: 'Please login to continue',
            type: 'success',
          }))
          .catch(() => notification({
            title: 'Unable to update password!',
            description:
              'Please ensure that the password reset link is correct',
            type: 'error',
          }));
      },
    }),
    changePassword: builder.mutation({
      query: ({ oldPassword, password, confirmPassword }) => ({
        url: '/users/changePassword',
        method: 'PUT',
        body: { oldPassword, password, confirmPassword },
      }),
    }),
    getAllUsers: builder.query({
      query: ({
        // role, searchUser, skip, limit,
        role, searchUser, skip, limit, isAdminVerified, labId
      }) => ({
        url: '/admin/getAllUsers',
        method: 'GET',
        params: {
          role,
          searchUser,
          skip,
          limit: 100,
          isAdminVerified,
          labId
        },
      }),
      providesTags: (_, __, arg) => (['labUser']),
      transformResponse: (res) => res?.data?.users,
    }),
    blockUnblockUser: builder.mutation({
      query: ({ id, role, isBlocked }) => ({
        url: '/admin/blockUnblockUser',
        method: 'PUT',
        body: { id, role, isBlocked },
      }),
      invalidatesTags: ['labUser'],
      onQueryStarted({ id, role, isBlocked }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          API.util.updateQueryData('getAllUsers', { role }, (draft) => {
            const i = draft.findIndex((el) => el._id === id);
            draft[i].isBlocked = !!isBlocked;
          }),
        );
        queryFulfilled
          .then(() => notification({
            title: 'Success!',
            description: isBlocked
              ? 'User blocked successfully'
              : 'User un-blocked successfully',
            type: 'success',
          }))
          .catch((err) => {
            notification({
              type: 'error',
              title: isBlocked
                ? 'Unable to block user!'
                : 'Unable to unblock user!',
              description:
                // 'Something went wrong. Please try again later after sometime.',
                err?.error?.data?.message
            });
            patchResult.undo();
          });
      },
    }),
    deleteUser: builder.mutation({
      query: ({ id, role, isDeleted }) => ({
        url: '/admin/deleteUser',
        method: 'PUT',
        body: { id, role, isDeleted },
      }),
      invalidatesTags: ['labUser'],
      onQueryStarted({ id, role }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          API.util.updateQueryData('getAllUsers', { role }, (draft) => {
            const i = draft.findIndex((el) => el._id === id);
            draft.splice(i, 1);
          }),
        );
        queryFulfilled.then(() => notification({
          title: 'Success!',
          description: 'User deleted successfully',
          type: 'success',
        })).catch(() => {
          notification({
            type: 'error',
            title: 'Unable to delete user!',
            description:
              'Something went wrong. Please try again later after sometime.',
          });
          patchResult.undo();
        });
      },
    }),

    getUserDetails: builder.query({
      query: ({ id }) => ({
        url: '/users/getUserDetails',
        method: 'GET',
        params: { id },
      }),
      transformResponse: (res) => res?.data?.user,
      providesTags: [''],
    }),

    updateProfile: builder.mutation({
      query: ({ role, ...updateData }) => ({
        url: '/users/updateProfile',
        method: 'PUT',
        body: updateData,
      }),
      invalidatesTags: (_, __, { role }) => ((role === 9 || role === 10) ? ['labUser'] : []),
    }),
    uploadFile: builder.mutation({
      query: (body) => ({
        url: '/common/uploadFile',
        method: 'POST',
        body,
      }),
    }),

    getDashboard: builder.query({
      query: () => ({
        url: '/lab/getDashboard',
        method: 'GET',
      }),
      transformResponse: (res) => res?.data,
    }),

    getTestList: builder.query({
      query: (params) => {
        let url = '/lab/getTestsList';
        if (params) {
          const queryString = new URLSearchParams(params).toString();
          if (queryString) {
            url += `?${queryString}`;
          }
        }
        return {
          url,
          method: 'GET',
        };
      },
      providesTags: ['test'],
      transformResponse: (res) => res?.data?.data,
    }),

    getTestDetailsById: builder.query({
      query: ({ id }) => ({
        url: `/lab/getTestDetails/${id}`,
        method: 'GET',
      }),
      transformResponse: (res) => res?.data,
    }),

    addTest: builder.mutation({
      query: (body) => ({
        url: '/admin/createTest',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['test'],
    }),

    updateTestDetails: builder.mutation({
      query: ({ id, body }) => {
        return {
          url: `/admin/updateTest/${id}`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: ['test'],
    }),

    deleteTest: builder.mutation({
      query: ({ id }) => {
        return {
          url: `/admin/deleteTest/${id}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['test'],
    }),

    blockUnblockTest: builder.mutation({
      query: ({ id, isBlocked }) => ({
        url: `/admin/blockUnblockTest/${id}`,
        method: 'PUT',
        body: { isBlocked },
      }),
      invalidatesTags: ['test'],
      onQueryStarted({ isBlocked }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          API.util.updateQueryData(
            'getTestList',
            { isBlocked },
            (draft) => {
              const i = draft.findIndex((el) => el._id === id);
              draft[i].isBlocked = !!isBlocked;
            },
          ),
        );
        queryFulfilled
          .then(() => notification({
            title: 'Success!',
            description: isBlocked
              ? 'Test blocked successfully'
              : 'Test un-blocked successfully',
            type: 'success',
          }))
          .catch(() => {
            notification({
              type: 'error',
              title: isBlocked
                ? 'Unable to block test!'
                : 'Unable to unblock test!',
              description:
                'Something went wrong. Please try again later after sometime.',
            });
            patchResult.undo();
          });
      },
    }),

    getPanelList: builder.query({
      query: (params) => {
        let url = '/lab/getPanelsList';
        if (params) {
          const queryString = new URLSearchParams(params).toString();
          if (queryString) {
            url += `?${queryString}`;
          }
        }
        return {
          url,
          method: 'GET',
        };
      },
      providesTags: ['panel'],
      transformResponse: (res) => res?.data?.data,
    }),

    getPanelDetailsById: builder.query({
      query: ({ id }) => ({
        url: `/lab/getPanelDetails/${id}`,
        method: 'GET',
      }),
      transformResponse: (res) => res?.data,
    }),

    addPanel: builder.mutation({
      query: (body) => ({
        url: '/admin/createPanel',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['panel'],
    }),

    updatePanelDetails: builder.mutation({
      query: ({ id, body }) => {
        return {
          url: `/admin/updatePanel/${id}`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: ['panel'],
    }),

    deletePanel: builder.mutation({
      query: ({ id }) => {
        return {
          url: `/admin/deletePanel/${id}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ["panel"],
    }),

    blockUnblockPanel: builder.mutation({
      query: ({ id, isBlocked }) => ({
        url: `/admin/blockUnblockPanel/${id}`,
        method: 'PUT',
        body: { isBlocked },
      }),
      invalidatesTags: ['panel'],
      onQueryStarted({ isBlocked }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          API.util.updateQueryData(
            'getPanelList',
            { isBlocked },
            (draft) => {
              const i = draft.findIndex((el) => el._id === id);
              draft[i].isBlocked = !!isBlocked;
            },
          ),
        );
        queryFulfilled
          .then(() => notification({
            title: 'Success!',
            description: isBlocked
              ? 'Panel blocked successfully'
              : 'Panel un-blocked successfully',
            type: 'success',
          }))
          .catch(() => {
            notification({
              type: 'error',
              title: isBlocked
                ? 'Unable to block panel!'
                : 'Unable to unblock panel!',
              description:
                'Something went wrong. Please try again later after sometime.',
            });
            patchResult.undo();
          });
      },
    }),

    getLabsList: builder.query({
      query: (params) => {
        let url = '/admin/getLabsList';
        if (params) {
          const queryString = new URLSearchParams(params).toString();
          if (queryString) {
            url += `?${queryString}`;
          }
        }
        return {
          url,
          method: 'GET',
        };
      },
      providesTags: ['lab'],
      transformResponse: (res) => res?.data?.data,
    }),

    getLabDetailsById: builder.query({
      query: ({ id }) => ({
        url: `/admin/getLabDetails/${id}`,
        method: 'GET',
      }),
      transformResponse: (res) => res?.data,
    }),

    createLab: builder.mutation({
      query: (body) => ({
        url: '/admin/createLab',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['lab'],
    }),

    updateLabDetails: builder.mutation({
      query: ({ id, body }) => {
        return {
          url: `/admin/updateLab/${id}`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: ['lab'],
    }),

    deleteLab: builder.mutation({
      query: ({ id }) => {
        return {
          url: `/admin/deleteLab/${id}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['lab'],
    }),

    blockUnblockLab: builder.mutation({
      query: ({ id, isBlocked }) => ({
        url: `/admin/blockUnblockLab/${id}`,
        method: 'PUT',
        body: { isBlocked },
      }),
      invalidatesTags: ['lab'],
      onQueryStarted({ isBlocked }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          API.util.updateQueryData(
            'getLabsList',
            { isBlocked },
            (draft) => {
              const i = draft.findIndex((el) => el._id === id);
              draft[i].isBlocked = !!isBlocked;
            },
          ),
        );
        queryFulfilled
          .then(() => notification({
            title: 'Success!',
            description: isBlocked
              ? 'Lab blocked successfully'
              : 'Lab un-blocked successfully',
            type: 'success',
          }))
          .catch(() => {
            notification({
              type: 'error',
              title: isBlocked
                ? 'Unable to block lab!'
                : 'Unable to unblock lab!',
              description:
                'Something went wrong. Please try again later after sometime.',
            });
            patchResult.undo();
          });
      },
    }),
    signUpUser: builder.mutation({
      query: (body) => ({
        url: '/users/signUp',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['labUser'],
    }),
    linkPanelToLab: builder.mutation({
      query: (body) => ({
        url: '/lab/link-panel',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['panel'],
    }),
    updatePanelPrice: builder.mutation({
      query: ({ body }) => {
        return {
          url: `/lab/updatePanelPrice`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: ['panel'],
    }),
    getBookingDetails: builder.query({
      query: (params) => {
        let url = '/booking/getBookingDetails';
        if (params) {
          const queryString = new URLSearchParams(params).toString();
          if (queryString) {
            url += `?${queryString}`;
          }
        }
        return {
          url,
          method: 'GET',
        };
      },
      transformResponse: (res) => res?.data,
    }),
    getLinkedPanels: builder.query({
      query: ({ labId }) => ({
        url: `/doctor/getLinkedPanels/${labId}`,
        method: 'GET',
      }),
      transformResponse: (res) => res?.data?.data,
    }),
    createLabOrder: builder.mutation({
      query: ({ body }) => ({
        url: '/doctor/createLabOrder',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['LabOrders'],
    }),
    getLabOrders: builder.query({
      query: (params) => {
        let url = '/lab/orders';
        if (params) {
          const queryString = new URLSearchParams(params).toString();
          if (queryString) {
            url += `?${queryString}`;
          }
        }
        return {
          url,
          method: 'GET',
        };
      },
      transformResponse: (res) => res?.data?.data,
      providesTags: ['LabOrders'],
    }),
    getLabOrdersById: builder.query({
      query: ({ id }) => ({
        url: `/lab/orders/${id}`,
        method: 'GET',
      }),
      transformResponse: (res) => res?.data,
    }),
    updateOrderStatus: builder.mutation({
      query: ({ body, id }) => {
        return {
          url: `/lab/orders/${id}`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: ['LabOrders'],
    }),
  }),
});

export const {
  useLoginMutation,
  useVerifyTwoFactorAuthMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useChangePasswordMutation,
  useBlockUnblockUserMutation,
  useDeleteUserMutation,
  useGetAllUsersQuery,
  useGetUserDetailsQuery,
  useLazyGetUserDetailsQuery,
  useUpdateProfileMutation,
  useUploadFileMutation,
  useGetDashboardQuery,
  useGetTestListQuery,
  useLazyGetTestListQuery,
  useGetTestDetailsByIdQuery,
  useLazyGetTestDetailsByIdQuery,
  useAddTestMutation,
  useUpdateTestDetailsMutation,
  useDeleteTestMutation,
  useBlockUnblockTestMutation,
  useGetPanelListQuery,
  useLazyGetPanelListQuery,
  useGetPanelDetailsByIdQuery,
  useLazyGetPanelDetailsByIdQuery,
  useAddPanelMutation,
  useUpdatePanelDetailsMutation,
  useDeletePanelMutation,
  useBlockUnblockPanelMutation,
  useGetLabsListQuery,
  useLazyGetLabsListQuery,
  useGetLabDetailsByIdQuery,
  useLazyGetLabDetailsByIdQuery,
  useCreateLabMutation,
  useUpdateLabDetailsMutation,
  useDeleteLabMutation,
  useBlockUnblockLabMutation,
  useSignUpUserMutation,
  useLinkPanelToLabMutation,
  useUpdatePanelPriceMutation,
  useGetBookingDetailsQuery,
  useLazyGetBookingDetailsQuery,
  useGetLinkedPanelsQuery,
  useLazyGetLinkedPanelsQuery,
  useCreateLabOrderMutation,
  useGetLabOrdersQuery,
  useLazyGetLabOrdersQuery,
  useGetLabOrdersByIdQuery,
  useLazyGetLabOrdersByIdQuery,
  useUpdateOrderStatusMutation,
} = API;
